import type { Game, Provider, ProviderSearchResult } from "../types/game";

const RAWG_KEY = import.meta.env.VITE_RAWG_API_KEY as string | undefined;

interface RawgGame {
  id: number;
  slug: string;
  name: string;
  released: string | null;
  background_image: string | null;
  rating: number;
  platforms?: Array<{ platform: { name: string } }>;
  genres?: Array<{ name: string }>;
  tags?: Array<{ name: string }>;
  stores?: Array<{ store: { name: string } }>;
  short_screenshots?: Array<{ image: string }>;
}

interface RawgListResponse {
  results: RawgGame[];
  count: number;
}

function normalize(g: RawgGame): Game {
  return {
    id: `rawg:${g.id}`,
    source: "rawg",
    sourceId: String(g.id),
    title: g.name,
    platforms: g.platforms?.map((p) => p.platform.name) ?? [],
    genres: g.genres?.map((x) => x.name) ?? [],
    tags: g.tags?.map((t) => t.name),
    releaseYear: g.released ? Number(g.released.slice(0, 4)) : undefined,
    imageUrl: g.background_image ?? undefined,
    rating: g.rating,
    stores: g.stores?.map((s) => s.store.name),
    screenshots: g.short_screenshots?.map((s) => s.image),
  };
}

export const rawgProvider: Provider = {
  id: "rawg",
  label: "RAWG",
  blurb:
    "Largest free game DB. CORS-friendly, single API key, broad metadata coverage.",
  async search(query: string): Promise<ProviderSearchResult> {
    if (!RAWG_KEY) {
      throw new Error(
        "Missing VITE_RAWG_API_KEY. Add it to your .env (see .env.example).",
      );
    }
    const url = new URL("https://api.rawg.io/api/games");
    url.searchParams.set("key", RAWG_KEY);
    url.searchParams.set("search", query);
    url.searchParams.set("page_size", "10");

    const res = await fetch(url.toString());
    if (!res.ok) {
      throw new Error(`RAWG request failed: ${res.status} ${res.statusText}`);
    }
    const raw = (await res.json()) as RawgListResponse;
    return { raw, games: raw.results.map(normalize) };
  },
  notes: {
    requiresApiKey: true,
    callableFromBrowser: "yes",
    corsStatus: "ok",
    needsProxy: false,
    provides: [
      "title",
      "platforms",
      "genres",
      "tags",
      "release date",
      "cover image",
      "screenshots",
      "stores",
      "rating",
    ],
    missing: [
      "crossplay",
      "online/local co-op",
      "split-screen",
      "max players",
      "Game Pass / PS Plus availability",
      "trailers (only via separate endpoint /games/{id}/movies)",
      "structured description (only on detail endpoint)",
    ],
    viability: "good",
    summary:
      "Best primary catalog source for the MVP. Direct browser calls work; key is technically public but that's how RAWG's free tier is designed. Co-op / crossplay must come from elsewhere.",
  },
};
